import type {
  ReasoningTrailOutcome,
  ReasoningTrailResult,
  ReasoningTrailStep,
} from '@hivewatch/dark'

import {
  AiOperatorReasoningResult,
  IncidentActionType,
} from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'

import type {
  ReasoningStep,
  ReasoningTrail,
} from '../types/reasoningTrailTypes'

const RESULT_MAP: Record<AiOperatorReasoningResult, ReasoningTrailResult> = {
  [AiOperatorReasoningResult.Yes]: 'YES',
  [AiOperatorReasoningResult.No]: 'NO',
  [AiOperatorReasoningResult.NotRequired]: 'NOT_REQUIRED',
}

// Matches an ISO-8601 date / date-time (optional time, optional Z/offset).
// Strict so plain text or bare numbers (e.g. "2024") aren't treated as dates.
const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/

/**
 * Formats a metadata row value for display: ISO-8601 dates are rendered via
 * `formatDateTime`; any other value is returned unchanged.
 */
export const formatMetadataValue = (value: string): string => {
  if (!ISO_DATE_PATTERN.test(value)) {
    return value
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return formatDateTime(value)
}

const mapStep = (step: ReasoningStep): ReasoningTrailStep => ({
  id: step.fieldId,
  label: step.label,
  result: RESULT_MAP[step.result],
  isDecisive: step.isDecisive,
  description: step.description,
  metadataCard: step.metadataCard
    ? {
        title: step.metadataCard.title,
        rows: step.metadataCard.rows.map((row) => ({
          label: row.label,
          value: formatMetadataValue(row.value),
        })),
      }
    : null,
  // ACS steps carry a trace-log link → surface the "View access logs"
  // affordance (wired in ENG-30872).
  hasAccessLogs: Boolean(step.traceLogLink),
})

/**
 * Adapts the server-computed GraphQL reasoning trail into the plain,
 * presentation-only props the `@hivewatch/dark` ReasoningTrailCard consumes.
 * Decisive-emphasis and glyph/label rendering are the dark card's concern; this
 * only normalizes the data (steps stay in BE-provided order).
 */
export const mapReasoningTrail = (
  trail: ReasoningTrail
): { steps: ReasoningTrailStep[]; outcome: ReasoningTrailOutcome } => ({
  steps: trail.steps.map(mapStep),
  outcome: {
    isResolved: trail.outcome.kind === IncidentActionType.Resolved,
    title: trail.outcome.title,
    description: trail.outcome.description,
  },
})
